import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useGetProjectByIdQuery } from "@generated";
import { Project } from "../types/Project";

const cleanResource = (raw: any, relationshipKeys: string[]) => {
  const result: Record<string, any> = {
    id: raw?.id,
    type: raw?.type,
    attributes: raw?.attributes,
  };
  if (relationshipKeys.length > 0) {
    const relationships: Record<string, any> = {};
    for (const key of relationshipKeys) {
      const rel = raw?.relationships?.[key];
      if (rel !== undefined) {
        relationships[key] = { data: rel?.data ?? null };
      }
    }
    result.relationships = relationships;
  }
  return result;
};

const useGetProject = (id) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  let customError: string | null = null;

  const { data, isLoading, error } = useGetProjectByIdQuery({
    slug: accountSlug,
    id,
    include: [
      /* PROJECT INFO */
      "payment-term",
      "rate-table",

      /* PROJECT SERVICES */
      "project-services",
      //@ts-ignore,
      "project-services.service",
      //@ts-ignore,
      "project-services.project-phase",
      //@ts-ignore,
      "project-services.project-resource",

      /* PROJECT PRODUCTS */
      "project-products",
      // @ts-ignore
      "project-products.product",
      // @ts-ignore
      "project-products.project-service",

      /* PROJECT GOVERNANCE */
      "project-governances",
      // @ts-ignore
      "project-governances.governance",
      // @ts-ignore
      "project-governances.project-resource",
      // @ts-ignore
      "project-governances.project-phase",

      /* PROJECT RESOURCES */
      "project-resources",
      // @ts-ignore
      "project-resources.line-of-business",

      /* PROJECT EXPENSE */
      "project-expenses",
      // @ts-ignore
      "project-expenses.expense-category",
      // @ts-ignore
      "project-expenses.project-phase",

      /* QUOTES */
      // "quotes",
      // "quotes.vendor",
    ],
  });

  let project: Project | undefined = undefined;

  if (data?.data) {
    try {
      const p = data.data;
      if (p?.id && p?.relationships) {
        const services =
          p.relationships?.["project-services"]?.data?.flatMap((ps) => {
            const service = data?.included?.find?.(
              (item) => item.type === "project-services" && item.id == ps.id
            ) as any;
            if (!service?.attributes?.active) return [];
            const resource = data?.included?.find?.(
              (item) =>
                item.type === "project-resources" &&
                item.id ==
                  service?.relationships?.["project-resource"]?.data?.id
            ) as any;
            return [
              {
                id: String(service?.id || ""),
                serviceType: service?.attributes?.["service-type"] || "",
                name: String(service?.attributes?.name || ""),
                selected: true,
                quantity: service?.attributes?.quantity,
                hours: service?.attributes?.["total-hours"],
                price:
                  service?.attributes?.["calculated-pricing"]?.[
                    "service_revenue"
                  ],
                cost: service?.attributes?.["calculated-pricing"]?.[
                  "service_cost"
                ],
                resource:
                  resource?.attributes?.["extended-name"] ||
                  resource?.attributes?.name ||
                  "",
                rawResource: cleanResource(service, [
                  "service",
                  "project-phase",
                  "project-resource",
                ]),
              },
            ];
          }) || [];

        // const quotes =
        //   p.relationships?.["quotes"]?.data?.flatMap((q) => {
        //     const quote = data?.included?.find?.(
        //       (item) => item.type === "quotes" && item.id == q.id
        //     ) as any;
        //     if (!quote?.attributes?.active) return [];
        //     const vendor = data?.included?.find?.(
        //       (item) =>
        //         item.type === "vendors" &&
        //         item.id == quote?.relationships?.["vendor"]?.data?.id
        //     ) as any;
        //     return [
        //       {
        //         id: String(quote?.id || ""),
        //         name: String(vendor?.attributes?.name || ""),
        //         selected: true,
        //         cost: quote?.attributes?.cost,
        //         recCost: quote?.attributes?.["recurring-cost"],
        //         revenue: quote?.attributes?.price,
        //         recRevenue: quote?.attributes?.["recurring-price"],
        //         rawResource: quote,
        //       },
        //     ];
        //   }) || [];

        const products =
          p.relationships?.["project-products"]?.data?.flatMap((p) => {
            const product = data?.included?.find?.(
              (item) => item.type === "project-products" && item.id == p.id
            ) as any;
            if (!product?.attributes?.active) return [];
            return [
              {
                id: String(product?.id || ""),
                name: String(product?.attributes?.name || ""),
                selected: true,
                quantity: product?.attributes?.quantity,
                mfrPart: product?.attributes?.["manufacturer-part-number"],
                units: product?.attributes?.["unit-of-measure"],
                price: product?.attributes?.["unit-price"],
                rawResource: cleanResource(product, [
                  "product",
                  "project-service",
                ]),
              },
            ];
          }) || [];

        const governances =
          p.relationships?.["project-governances"]?.data?.flatMap((g) => {
            const governance = data?.included?.find?.(
              (item) => item.type === "project-governances" && item.id == g.id
            ) as any;
            if (!governance?.attributes?.active) return [];
            const resource = data?.included?.find?.(
              (item) =>
                item.type === "project-resources" &&
                item.id ==
                  governance?.relationships?.["project-resource"]?.data?.id
            ) as any;
            const phase = data?.included?.find?.(
              (item) =>
                item.type === "project-phases" &&
                item.id ==
                  governance?.relationships?.["project-phase"]?.data?.id
            ) as any;
            return [
              {
                id: String(governance?.id || ""),
                name: String(governance?.attributes?.description || ""),
                selected: true,
                resource: resource?.attributes?.name || "",
                phase: phase?.attributes?.name || "",
                calculation: governance?.attributes?.["calculation-type"] || "",
                hours: governance?.attributes?.hours,
                rawResource: cleanResource(governance, [
                  "governance",
                  "project-resource",
                  "project-phase",
                ]),
              },
            ];
          }) || [];

        const resources =
          p.relationships?.["project-resources"]?.data?.flatMap((r) => {
            const resource = data?.included?.find?.(
              (item) => item.type === "project-resources" && item.id == r.id
            ) as any;
            if (!resource?.attributes?.active) return [];
            const lob = data?.included?.find?.(
              (item) =>
                item.type === "line-of-businesses" &&
                item.id ==
                  resource?.relationships?.["line-of-business"]?.data?.id
            ) as any;
            return [
              {
                id: String(resource?.id || ""),
                name: String(resource?.attributes?.name || ""),
                lob: lob?.attributes?.["nested-name"] || "",
                hourlyRate: resource?.attributes?.["hourly-rate"],
                expenseRate: resource?.attributes?.["expense-rate"],
                selected: true,
                rawResource: cleanResource(resource, []),
              },
            ];
          }) || [];

        const expenses =
          p.relationships?.["project-expenses"]?.data?.flatMap((e) => {
            const expense = data?.included?.find?.(
              (item) => item.type === "project-expenses" && item.id == e.id
            ) as any;
            if (!expense?.attributes?.active) return [];
            const expenseCategory = data?.included?.find?.(
              (item) =>
                item.type === "expense-categories" &&
                item.id ==
                  expense?.relationships?.["expense-category"]?.data?.id
            ) as any;
            return [
              {
                id: String(expense?.id || ""),
                name: String(expenseCategory?.attributes?.name || ""),
                selected: true,
                quantity: expense?.attributes?.quantity,
                cost: expense?.attributes?.cost,
                rawResource: cleanResource(expense, [
                  "expense-category",
                  "project-phase",
                ]),
              },
            ];
          }) || [];

        project = {
          id: p.id,
          executiveSummary: p.attributes?.["executive-summary"] || "",
          solutionSummary: p.attributes?.["solution-summary"] || "",
          paymentTermId: p.relationships?.["payment-term"]?.data?.id,
          rateTableId: p.relationships?.["rate-table"]?.data?.id,
          services,
          // quotes: [],
          products,
          governances,
          resources,
          expenses,
        };
      } else {
        customError = "Project data is missing or malformed";
      }
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  return {
    project,
    isLoading,
    error: error ?? customError,
  };
};
export default useGetProject;
